import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from '../../shared/utils.service';
import { ApiToken, ApiTokensService } from './api-tokens.service';
import { ApiTokenCreateDialogComponent } from './api-token-create-dialog/api-token-create-dialog.component';

@Component({
  selector: 'app-api-tokens',
  templateUrl: './api-tokens.component.html',
  styleUrls: ['./api-tokens.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ApiTokensComponent implements OnInit {
  // The user's Personal API tokens.
  tokens: ApiToken[] = [];

  // Whether the initial load has completed (used to show an empty state only after loading).
  loaded: boolean = false;

  // Whether the user can access token management (core user profile permission).
  canManage: boolean = false;

  displayedColumns: string[] = [
    'name',
    'created_at',
    'last_used_at',
    'expires_at',
    'actions'
  ];

  constructor(
    public rbac: RBACService,
    public utils: UtilsService,
    private apiTokens: ApiTokensService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Every authenticated user holds the core user profile permission, but the guard is kept
    // explicit for consistency and to drive the access-denied path.
    this.canManage = this.rbac.userCanAccess([
      RBACService.RBAC_F.CORE_PERMISSION_USER_PROFILE
    ]);

    if (!this.canManage) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage API tokens.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadTokens();
  }

  /**
   * Load the user's Personal API tokens.
   */
  loadTokens(): void {
    this.apiTokens
      .list()
      .then((tokens: ApiToken[]) => {
        this.tokens = tokens;
      })
      .catch((error: any) => this.handleError(error))
      .finally(() => {
        this.loaded = true;
      });
  }

  /**
   * Open the create dialog and refresh the list when a token is created.
   */
  onGenerate(): void {
    if (!this.canManage) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create API tokens.'
      );
      return;
    }

    const dialogRef = this.dialog.open(ApiTokenCreateDialogComponent, {
      width: '520px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((created: boolean) => {
      if (created) {
        this.loadTokens();
      }
    });
  }

  /**
   * Revoke a token after confirmation.
   */
  onRevoke(token: ApiToken): void {
    if (!this.canManage) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to revoke API tokens.'
      );
      return;
    }

    this.utils.showModal(
      'Revoke API Token',
      `Are you sure you want to revoke "${
        token.name
      }"? Any integration using this token will immediately stop working. This cannot be undone.`,
      () => {
        this.apiTokens
          .revoke(token.id)
          .then(() => {
            this.utils.showToast('API token revoked.');
            this.tokens = this.tokens.filter(
              (t: ApiToken) => t.id !== token.id
            );
          })
          .catch((error: any) => this.handleError(error));
      }
    );
  }

  /**
   * Handle API errors, surfacing a friendly access-denied message on 403.
   */
  private handleError(error: any): void {
    if (error && error.status === 403) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage API tokens.'
      );
      return;
    }
    this.utils.handleAPIErrors(error);
  }
}
